import React, { useEffect, useState } from "react";
import axios from "axios";
import swal from "sweetalert";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

const API_KEY =
  "eyJ0eXAiOiJKV1Qidfg%!#%$%LCdf#*&*JhbGciOiJSUzI1NiJ9";

const BasicDatatable = () => {
  const { t } = useTranslation();

  const [pageLoading, setPageLoading] = useState(true);
  const [securityCk, setSecurityCk] = useState(null);
  const [pageError, setPageError] = useState("");

  const [emailSending, setEmailSending] = useState(false);
  const [emailSubmitting, setEmailSubmitting] = useState(false);
  const [emailMessage, setEmailMessage] = useState("");

  const [depositInput, setDepositInput] = useState({
    email_code: "",
    error_list: {},
  });

  const authToken = localStorage.getItem("auth_token");

  /* =========================================================
     LOAD SECURITY / KYC STATUS
     No full-page loader: only data areas use skeletons.
  ========================================================= */
  const loadSecurity = async () => {
    const selectedCurrency = localStorage.getItem("selectedCurrency");

    if (!authToken) {
      setPageLoading(false);
      setPageError("Your session has expired. Please login again.");
      return;
    }

    try {
      setPageLoading(true);
      setPageError("");

      const response = await axios.post("/security-home", {
        key_s: API_KEY,
        currency: selectedCurrency,
      });

      setSecurityCk(response?.data || null);
    } catch (error) {
      console.error("Security home error:", error);
      setPageError(
        error?.response?.data?.message ||
          "Unable to load your security information."
      );
    } finally {
      setPageLoading(false);
    }
  };

  useEffect(() => {
    loadSecurity();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const securityData = securityCk?.data || {};
  const identityStatus = Number(securityData?.identity_status ?? 0);
  const mailStatus = Number(securityData?.mail_status ?? 0);

  const emailVerified = mailStatus === 1;
  const identityVerified = identityStatus === 1;
  const identitySubmitted = identityStatus === 2;
  const identityResubmit = identityStatus === 3;

  /* =========================================================
     EMAIL VERIFICATION
  ========================================================= */
  const handleInput = (e) => {
    const { name, value } = e.target;

    setDepositInput((previous) => ({
      ...previous,
      [name]: value,
      error_list: {
        ...previous.error_list,
        [name]: "",
      },
    }));
  };

  const handleSendEmailCode = async () => {
    if (!authToken || emailSending) return;

    try {
      setEmailSending(true);
      setEmailMessage("");

      const res = await axios.post("/email-account-verify", {
        key_s: API_KEY,
        root_url: "https://account.rajyaproperties.com/",
      });

      if (res?.data?.status === "success") {
        setEmailMessage(
          res.data.message ||
            "Verification code sent. Please check your email."
        );
        setDepositInput((previous) => ({
          ...previous,
          email_code: "",
          error_list: {},
        }));
      } else {
        swal(
          "Warning",
          res?.data?.message || "Unable to send verification email.",
          "warning"
        );
      }
    } catch (error) {
      swal(
        "Error",
        error?.response?.data?.message ||
          "Unable to send verification email.",
        "error"
      );
    } finally {
      setEmailSending(false);
    }
  };

  const depositSubmit = async (e) => {
    e.preventDefault();

    if (!authToken || emailSubmitting) return;

    if (!depositInput.email_code.trim()) {
      setDepositInput((previous) => ({
        ...previous,
        error_list: {
          ...previous.error_list,
          email_code: "Email verification code is required.",
        },
      }));
      return;
    }

    try {
      setEmailSubmitting(true);

      const res = await axios.post("/email-account-verify-sub", {
        email_code: depositInput.email_code.trim(),
        key_s: API_KEY,
      });

      if (res?.data?.status === "success") {
        setEmailMessage("");
        setDepositInput({
          email_code: "",
          error_list: {},
        });

        await swal(
          "Email Verified",
          res.data.message || "Your email has been verified successfully.",
          "success"
        );

        await loadSecurity();
      } else if (res?.data?.status === "fail") {
        swal(
          "Warning",
          res?.data?.message || "Invalid verification code.",
          "warning"
        );
      } else {
        setDepositInput((previous) => ({
          ...previous,
          error_list: res?.data?.validation_error || {},
        }));
      }
    } catch (error) {
      if (error?.response?.status === 422) {
        setDepositInput((previous) => ({
          ...previous,
          error_list:
            error?.response?.data?.errors ||
            error?.response?.data?.validation_error ||
            {},
        }));
      } else {
        swal(
          "Error",
          error?.response?.data?.message ||
            "Unable to verify your email.",
          "error"
        );
      }
    } finally {
      setEmailSubmitting(false);
    }
  };

  /* =========================================================
     IDENTITY STATUS
  ========================================================= */
  const getIdentityStatus = () => {
    if (identityVerified) {
      return {
        className: "kyc-success",
        icon: "fas fa-check-circle",
        title: "Verified",
        text: "Your identity verification is complete.",
      };
    }

    if (identitySubmitted) {
      return {
        className: "kyc-pending",
        icon: "fas fa-clock",
        title: "Submitted",
        text: "Your KYC has been submitted and is under review.",
      };
    }

    if (identityResubmit) {
      return {
        className: "kyc-danger",
        icon: "fas fa-redo-alt",
        title: "Resubmit Required",
        text: "Please update and resubmit your identity information.",
      };
    }

    return {
      className: "kyc-danger",
      icon: "fas fa-exclamation-circle",
      title: "Not Verified",
      text: "Complete your identity verification to secure your account.",
    };
  };

  const identityInfo = getIdentityStatus();

  return (
    <>
      <SecurityKycCSS />

      <main className="kyc-page">
        <div className="kyc-container">
          {/* HEADER */}
          <section className="kyc-header">
            <div className="kyc-header-copy">
              <span className="kyc-eyebrow">
                <i className="fas fa-shield-alt"></i>
                ACCOUNT SECURITY
              </span>

              <h1>Security &amp; KYC Center</h1>

              <p>
                Manage your password, verify your email and complete identity
                verification from one clear page.
              </p>
            </div>

            <div className="kyc-security-badge">
              <div className="kyc-security-icon">
                <i className="fas fa-user-shield"></i>
              </div>
              <div>
                <small>Security Center</small>
                <strong>Account Protection</strong>
              </div>
            </div>
          </section>

          {/* API ERROR */}
          {!pageLoading && pageError && (
            <div className="kyc-alert kyc-alert-danger">
              <i className="fas fa-exclamation-circle"></i>
              <div>
                <strong>Unable to load security status</strong>
                <span>{pageError}</span>
              </div>
              <button type="button" onClick={loadSecurity}>
                <i className="fas fa-sync-alt"></i>
                Retry
              </button>
            </div>
          )}

          {/* ONE PAGE / THREE SECTIONS */}
          <section className="kyc-all-fields">
            <div className="kyc-section-heading">
              <div>
                <span>ONE PAGE SECURITY SETUP</span>
                <h2>Complete all security information here</h2>
                <p>
                  No step-by-step screen. All available security and KYC
                  sections are visible on this page.
                </p>
              </div>

              {pageLoading ? (
                <span className="kyc-skeleton-line kyc-sk-progress"></span>
              ) : (
                <div className="kyc-completion">
                  <i className="fas fa-check-double"></i>
                  <span>
                    {[emailVerified, identityVerified].filter(Boolean).length}/2
                    verifications complete
                  </span>
                </div>
              )}
            </div>

            <div className="kyc-grid">
              {/* CHANGE PASSWORD */}
              <article className="kyc-card">
                <div className="kyc-card-top">
                  <div className="kyc-card-icon kyc-icon-purple">
                    <i className="fas fa-lock"></i>
                  </div>

                  <span className="kyc-status kyc-neutral">
                    <i className="fas fa-shield-alt"></i>
                    Security
                  </span>
                </div>

                <div className="kyc-card-content">
                  <span className="kyc-card-number">01</span>
                  <h3>{t("pro_security_ch_ps") || "Change Password"}</h3>
                  <p>
                    {t("pro_security_click_ch_ps") ||
                      "Keep your account protected by using a strong and unique password."}
                  </p>
                </div>

                <Link to="/change-password" className="kyc-action-btn">
                  <span>Change Password</span>
                  <i className="fas fa-arrow-right"></i>
                </Link>
              </article>

              {/* EMAIL VERIFICATION - FULL FIELD ON PAGE */}
              <article className="kyc-card kyc-email-card">
                <div className="kyc-card-top">
                  <div className="kyc-card-icon kyc-icon-green">
                    <i className="fas fa-envelope"></i>
                  </div>

                  {pageLoading ? (
                    <span className="kyc-skeleton-line kyc-sk-status"></span>
                  ) : (
                    <span
                      className={`kyc-status ${
                        emailVerified ? "kyc-success" : "kyc-danger"
                      }`}
                    >
                      <i
                        className={
                          emailVerified
                            ? "fas fa-check-circle"
                            : "fas fa-exclamation-circle"
                        }
                      ></i>
                      {emailVerified ? "Verified" : "Not Verified"}
                    </span>
                  )}
                </div>

                <div className="kyc-card-content">
                  <span className="kyc-card-number">02</span>
                  <h3>Email Verification</h3>
                  <p>
                    Verify your email address to protect account recovery and
                    important security notifications.
                  </p>
                </div>

                {pageLoading ? (
                  <div className="kyc-local-skeleton">
                    <span className="kyc-skeleton-line kyc-sk-button"></span>
                    <span className="kyc-skeleton-line kyc-sk-input"></span>
                    <span className="kyc-skeleton-line kyc-sk-submit"></span>
                  </div>
                ) : emailVerified ? (
                  <div className="kyc-complete-box">
                    <i className="fas fa-check-circle"></i>
                    <div>
                      <strong>Email Verified</strong>
                      <span>Your email verification is complete.</span>
                    </div>
                  </div>
                ) : (
                  <div className="kyc-inline-form">
                    <button
                      type="button"
                      className="kyc-send-code"
                      onClick={handleSendEmailCode}
                      disabled={emailSending}
                    >
                      <i
                        className={
                          emailSending
                            ? "fas fa-spinner fa-spin"
                            : "fas fa-paper-plane"
                        }
                      ></i>
                      {emailSending
                        ? "Sending Verification Code..."
                        : "Send Verification Code"}
                    </button>

                    {emailMessage && (
                      <div className="kyc-message">
                        <i className="fas fa-check-circle"></i>
                        <span>{emailMessage}</span>
                      </div>
                    )}

                    <form onSubmit={depositSubmit}>
                      <label htmlFor="email_code">
                        Email Verification Code
                      </label>

                      <div
                        className={`kyc-input-wrap ${
                          depositInput.error_list?.email_code
                            ? "kyc-input-error"
                            : ""
                        }`}
                      >
                        <i className="fas fa-key"></i>
                        <input
                          id="email_code"
                          type="text"
                          name="email_code"
                          placeholder="Enter verification code"
                          value={depositInput.email_code}
                          onChange={handleInput}
                          autoComplete="one-time-code"
                        />
                      </div>

                      {depositInput.error_list?.email_code && (
                        <span className="kyc-error-text">
                          {Array.isArray(
                            depositInput.error_list.email_code
                          )
                            ? depositInput.error_list.email_code[0]
                            : depositInput.error_list.email_code}
                        </span>
                      )}

                      <button
                        type="submit"
                        className="kyc-verify-btn"
                        disabled={
                          emailSubmitting ||
                          !depositInput.email_code.trim()
                        }
                      >
                        {emailSubmitting ? (
                          <>
                            <i className="fas fa-spinner fa-spin"></i>
                            Verifying...
                          </>
                        ) : (
                          <>
                            <i className="fas fa-check-circle"></i>
                            Verify Email
                          </>
                        )}
                      </button>
                    </form>
                  </div>
                )}
              </article>

              {/* IDENTITY / KYC */}
              <article className="kyc-card">
                <div className="kyc-card-top">
                  <div className="kyc-card-icon kyc-icon-gold">
                    <i className="fas fa-id-card"></i>
                  </div>

                  {pageLoading ? (
                    <span className="kyc-skeleton-line kyc-sk-status"></span>
                  ) : (
                    <span className={`kyc-status ${identityInfo.className}`}>
                      <i className={identityInfo.icon}></i>
                      {identityInfo.title}
                    </span>
                  )}
                </div>

                <div className="kyc-card-content">
                  <span className="kyc-card-number">03</span>
                  <h3>{t("pro_security_nid") || "Identity Verification"}</h3>
                  <p>
                    {t("pro_security_nid_tc") ||
                      "Submit your identity information and required documents for KYC verification."}
                  </p>
                </div>

                {pageLoading ? (
                  <div className="kyc-local-skeleton">
                    <span className="kyc-skeleton-line kyc-sk-info"></span>
                    <span className="kyc-skeleton-line kyc-sk-button"></span>
                  </div>
                ) : (
                  <>
                    <div
                      className={`kyc-identity-note ${identityInfo.className}`}
                    >
                      <i className={identityInfo.icon}></i>
                      <span>{identityInfo.text}</span>
                    </div>

                    {(identityStatus === 0 || identityStatus === 3) && (
                      <Link
                        to="/identity-verification"
                        className="kyc-action-btn"
                      >
                        <span>
                          {identityStatus === 3
                            ? "Resubmit KYC"
                            : "Complete KYC"}
                        </span>
                        <i className="fas fa-arrow-right"></i>
                      </Link>
                    )}

                    {identityStatus === 2 && (
                      <button
                        type="button"
                        className="kyc-action-btn kyc-action-disabled"
                        disabled
                      >
                        <span>KYC Under Review</span>
                        <i className="fas fa-clock"></i>
                      </button>
                    )}

                    {identityStatus === 1 && (
                      <div className="kyc-complete-box">
                        <i className="fas fa-check-circle"></i>
                        <div>
                          <strong>Identity Verified</strong>
                          <span>Your KYC verification is complete.</span>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </article>
            </div>
          </section>

          <section className="kyc-footer-note">
            <div className="kyc-footer-icon">
              <i className="fas fa-shield-alt"></i>
            </div>
            <div>
              <strong>Keep your account secure</strong>
              <p>
                Never share your password or verification codes with anyone.
                Complete email and identity verification to improve account
                security.
              </p>
            </div>
          </section>
        </div>
      </main>
    </>
  );
};

const SecurityKycCSS = () => (
  <style>{`
    * {
      box-sizing: border-box;
    }

    .kyc-page {
      --kyc-primary: #16382d;
      --kyc-primary-dark: #0d281f;
      --kyc-green: #12b76a;
      --kyc-red: #e5484d;
      --kyc-gold: #c89b4b;
      --kyc-purple: #635bff;
      --kyc-orange: #f79009;
      --kyc-text: #182230;
      --kyc-muted: #667085;
      --kyc-border: #e7e9ef;
      --kyc-bg: #f7f8fa;

      min-height: 100vh;
      padding: 20px;
      background: #fff;
      color: var(--kyc-text);
      font-family:
        Inter,
        -apple-system,
        BlinkMacSystemFont,
        "Segoe UI",
        sans-serif;
    }

    .kyc-container {
      width: 100%;
      max-width: 1280px;
      margin: 0 auto;
    }

    /* HEADER */
    .kyc-header {
      position: relative;
      overflow: hidden;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 30px;
      padding: 32px;
      margin-bottom: 22px;
      border-radius: 22px;
      background:
        radial-gradient(
          circle at 100% 0,
          rgba(200,155,75,.22),
          transparent 34%
        ),
        linear-gradient(
          135deg,
          var(--kyc-primary-dark),
          var(--kyc-primary)
        );
      box-shadow: 0 16px 45px rgba(13,40,31,.14);
    }

    .kyc-header::after {
      content: "";
      position: absolute;
      width: 250px;
      height: 250px;
      right: -90px;
      bottom: -160px;
      border-radius: 50%;
      background: rgba(255,255,255,.05);
    }

    .kyc-header-copy {
      position: relative;
      z-index: 2;
      max-width: 720px;
    }

    .kyc-eyebrow {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 10px;
      color: #e3c687;
      font-size: 13px;
      font-weight: 800;
      letter-spacing: .8px;
    }

    .kyc-header h1 {
      margin: 0 0 10px;
      color: #fff;
      font-size: clamp(28px, 4vw, 40px);
      line-height: 1.12;
      font-weight: 850;
    }

    .kyc-header p {
      max-width: 650px;
      margin: 0;
      color: rgba(255,255,255,.72);
      font-size: 15px;
      line-height: 1.7;
    }

    .kyc-security-badge {
      position: relative;
      z-index: 2;
      flex: 0 0 auto;
      min-width: 230px;
      display: flex;
      align-items: center;
      gap: 13px;
      padding: 17px;
      border: 1px solid rgba(255,255,255,.12);
      border-radius: 17px;
      background: rgba(255,255,255,.08);
      backdrop-filter: blur(10px);
    }

    .kyc-security-icon {
      flex: 0 0 45px;
      width: 45px;
      height: 45px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 13px;
      background: rgba(200,155,75,.18);
      color: #e5c47d;
      font-size: 19px;
    }

    .kyc-security-badge small,
    .kyc-security-badge strong {
      display: block;
    }

    .kyc-security-badge small {
      margin-bottom: 3px;
      color: rgba(255,255,255,.56);
      font-size: 12px;
    }

    .kyc-security-badge strong {
      color: #fff;
      font-size: 15px;
      font-weight: 800;
    }

    /* ALERT */
    .kyc-alert {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 15px 17px;
      margin-bottom: 20px;
      border-radius: 14px;
      font-size: 14px;
    }

    .kyc-alert-danger {
      border: 1px solid #ffd4d6;
      background: #fff4f4;
      color: #a72c31;
    }

    .kyc-alert > i {
      font-size: 20px;
    }

    .kyc-alert > div {
      flex: 1;
    }

    .kyc-alert strong,
    .kyc-alert span {
      display: block;
    }

    .kyc-alert strong {
      margin-bottom: 2px;
      font-size: 14px;
    }

    .kyc-alert span {
      font-size: 13px;
    }

    .kyc-alert button {
      display: inline-flex;
      align-items: center;
      gap: 7px;
      padding: 8px 12px;
      border: 0;
      border-radius: 9px;
      background: #fff;
      color: #a72c31;
      font-weight: 750;
      cursor: pointer;
    }

    /* MAIN SECTION */
    .kyc-all-fields {
      padding: 25px;
      border: 1px solid var(--kyc-border);
      border-radius: 22px;
      background: #fff;
      box-shadow: 0 10px 35px rgba(31,41,55,.055);
    }

    .kyc-section-heading {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 20px;
      padding-bottom: 20px;
      margin-bottom: 22px;
      border-bottom: 1px solid #eef0f3;
    }

    .kyc-section-heading > div:first-child > span {
      display: block;
      margin-bottom: 5px;
      color: var(--kyc-gold);
      font-size: 12px;
      font-weight: 850;
      letter-spacing: .6px;
    }

    .kyc-section-heading h2 {
      margin: 0 0 6px;
      color: var(--kyc-text);
      font-size: 24px;
      font-weight: 850;
    }

    .kyc-section-heading p {
      margin: 0;
      color: var(--kyc-muted);
      font-size: 14px;
      line-height: 1.6;
    }

    .kyc-completion {
      flex: 0 0 auto;
      display: inline-flex;
      align-items: center;
      gap: 8px;
      padding: 10px 13px;
      border-radius: 12px;
      background: #effaf4;
      color: #18794e;
      font-size: 13px;
      font-weight: 800;
    }

    .kyc-grid {
      display: grid;
      grid-template-columns: repeat(3, minmax(0, 1fr));
      gap: 18px;
      align-items: stretch;
    }

    /* CARDS */
    .kyc-card {
      min-width: 0;
      display: flex;
      flex-direction: column;
      padding: 21px;
      border: 1px solid var(--kyc-border);
      border-radius: 18px;
      background: #fff;
      transition: .2s ease;
    }

    .kyc-card:hover {
      transform: translateY(-2px);
      border-color: #d9dde5;
      box-shadow: 0 12px 28px rgba(31,41,55,.07);
    }

    .kyc-card-top {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 10px;
      margin-bottom: 19px;
    }

    .kyc-card-icon {
      width: 48px;
      height: 48px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 14px;
      font-size: 19px;
    }

    .kyc-icon-purple {
      background: #f0efff;
      color: var(--kyc-purple);
    }

    .kyc-icon-green {
      background: #ebfaf2;
      color: var(--kyc-green);
    }

    .kyc-icon-gold {
      background: #fff7e9;
      color: #ad7625;
    }

    .kyc-status {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 6px 9px;
      border-radius: 999px;
      font-size: 12px;
      font-weight: 800;
      white-space: nowrap;
    }

    .kyc-neutral {
      background: #f1f3f5;
      color: #667085;
    }

    .kyc-success {
      background: #ecf9f2;
      color: #168354;
    }

    .kyc-danger {
      background: #fff0f0;
      color: #cf3439;
    }

    .kyc-pending {
      background: #fff7e8;
      color: #a66a12;
    }

    .kyc-card-content {
      flex: 1;
    }

    .kyc-card-number {
      display: block;
      margin-bottom: 5px;
      color: #b0b6c0;
      font-size: 12px;
      font-weight: 850;
    }

    .kyc-card h3 {
      margin: 0 0 8px;
      color: var(--kyc-text);
      font-size: 20px;
      font-weight: 850;
    }

    .kyc-card-content p {
      margin: 0 0 20px;
      color: var(--kyc-muted);
      font-size: 14px;
      line-height: 1.65;
    }

    /* ACTION BUTTON */
    .kyc-action-btn,
    .kyc-verify-btn,
    .kyc-send-code {
      width: 100%;
      min-height: 47px;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 9px;
      border: 0;
      border-radius: 12px;
      text-decoration: none !important;
      font-size: 14px;
      font-weight: 800;
      cursor: pointer;
      transition: .18s ease;
    }

    .kyc-action-btn {
      margin-top: auto;
      background: var(--kyc-primary);
      color: #fff !important;
    }

    .kyc-action-btn:hover {
      background: var(--kyc-primary-dark);
      transform: translateY(-1px);
    }

    .kyc-action-disabled {
      opacity: .62;
      cursor: not-allowed;
    }

    /* EMAIL FORM */
    .kyc-inline-form {
      margin-top: auto;
      padding-top: 2px;
    }

    .kyc-send-code {
      margin-bottom: 13px;
      border: 1px solid #cfe7db;
      background: #effaf4;
      color: #18794e;
    }

    .kyc-send-code:hover:not(:disabled) {
      background: #e3f6ec;
    }

    .kyc-send-code:disabled,
    .kyc-verify-btn:disabled {
      opacity: .55;
      cursor: not-allowed;
    }

    .kyc-message {
      display: flex;
      align-items: flex-start;
      gap: 8px;
      padding: 10px 11px;
      margin-bottom: 12px;
      border: 1px solid #cdebdc;
      border-radius: 10px;
      background: #f1fbf6;
      color: #18794e;
      font-size: 12px;
      line-height: 1.5;
    }

    .kyc-inline-form label {
      display: block;
      margin-bottom: 7px;
      color: #344054;
      font-size: 13px;
      font-weight: 800;
    }

    .kyc-input-wrap {
      height: 49px;
      display: flex;
      align-items: center;
      overflow: hidden;
      border: 1.5px solid #dfe3e8;
      border-radius: 12px;
      background: #fff;
      transition: .18s;
    }

    .kyc-input-wrap:focus-within {
      border-color: var(--kyc-primary);
      box-shadow: 0 0 0 4px rgba(22,56,45,.07);
    }

    .kyc-input-wrap > i {
      width: 46px;
      text-align: center;
      color: #98a2b3;
      font-size: 14px;
    }

    .kyc-input-wrap input {
      width: 100%;
      height: 100%;
      padding: 0 13px 0 0;
      border: 0;
      outline: 0;
      background: transparent;
      color: var(--kyc-text);
      font-size: 15px;
      font-weight: 650;
    }

    .kyc-input-error {
      border-color: var(--kyc-red);
    }

    .kyc-error-text {
      display: block;
      margin-top: 6px;
      color: var(--kyc-red);
      font-size: 12px;
      font-weight: 650;
    }

    .kyc-verify-btn {
      margin-top: 12px;
      background: linear-gradient(
        135deg,
        var(--kyc-primary),
        #235848
      );
      color: #fff;
      box-shadow: 0 8px 18px rgba(22,56,45,.15);
    }

    /* IDENTITY */
    .kyc-identity-note {
      display: flex;
      align-items: flex-start;
      gap: 8px;
      padding: 11px;
      margin-bottom: 13px;
      border-radius: 11px;
      font-size: 12px;
      line-height: 1.5;
    }

    .kyc-complete-box {
      display: flex;
      align-items: center;
      gap: 11px;
      padding: 13px;
      margin-top: auto;
      border: 1px solid #cdebdc;
      border-radius: 12px;
      background: #f1fbf6;
      color: #18794e;
    }

    .kyc-complete-box > i {
      font-size: 23px;
    }

    .kyc-complete-box strong,
    .kyc-complete-box span {
      display: block;
    }

    .kyc-complete-box strong {
      margin-bottom: 2px;
      font-size: 14px;
    }

    .kyc-complete-box span {
      font-size: 12px;
      color: #47705a;
    }

    /* AREA-BASED LOADING */
    .kyc-skeleton-line {
      display: block;
      position: relative;
      overflow: hidden;
      border-radius: 999px;
      background: #edf0f3;
    }

    .kyc-skeleton-line::after {
      content: "";
      position: absolute;
      inset: 0;
      transform: translateX(-100%);
      background: linear-gradient(
        90deg,
        transparent,
        rgba(255,255,255,.88),
        transparent
      );
      animation: kyc-shimmer 1.2s infinite;
    }

    @keyframes kyc-shimmer {
      100% {
        transform: translateX(100%);
      }
    }

    .kyc-sk-progress {
      width: 170px;
      height: 34px;
    }

    .kyc-sk-status {
      width: 95px;
      height: 28px;
    }

    .kyc-local-skeleton {
      margin-top: auto;
    }

    .kyc-sk-button {
      width: 100%;
      height: 47px;
      margin-bottom: 13px;
      border-radius: 12px;
    }

    .kyc-sk-input {
      width: 100%;
      height: 49px;
      margin-bottom: 12px;
      border-radius: 12px;
    }

    .kyc-sk-submit {
      width: 100%;
      height: 47px;
      border-radius: 12px;
    }

    .kyc-sk-info {
      width: 100%;
      height: 60px;
      margin-bottom: 13px;
      border-radius: 11px;
    }

    /* FOOTER */
    .kyc-footer-note {
      display: flex;
      align-items: flex-start;
      gap: 13px;
      padding: 17px;
      margin-top: 20px;
      border: 1px solid #e4e8e6;
      border-radius: 15px;
      background: #fafcfb;
    }

    .kyc-footer-icon {
      flex: 0 0 40px;
      width: 40px;
      height: 40px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 11px;
      background: #eaf4ef;
      color: var(--kyc-primary);
    }

    .kyc-footer-note strong {
      display: block;
      margin-bottom: 3px;
      font-size: 14px;
      color: var(--kyc-text);
    }

    .kyc-footer-note p {
      margin: 0;
      color: var(--kyc-muted);
      font-size: 13px;
      line-height: 1.6;
    }

    /* RESPONSIVE */
    @media (max-width: 1050px) {
      .kyc-grid {
        grid-template-columns: repeat(2, minmax(0, 1fr));
      }

      .kyc-card:last-child {
        grid-column: 1 / -1;
      }
    }

    @media (max-width: 760px) {
      .kyc-page {
        padding: 12px;
      }

      .kyc-header {
        align-items: flex-start;
        flex-direction: column;
        padding: 24px 20px;
        border-radius: 18px;
      }

      .kyc-security-badge {
        width: 100%;
        min-width: 0;
      }

      .kyc-all-fields {
        padding: 17px;
        border-radius: 18px;
      }

      .kyc-section-heading {
        align-items: flex-start;
        flex-direction: column;
      }

      .kyc-grid {
        grid-template-columns: 1fr;
      }

      .kyc-card:last-child {
        grid-column: auto;
      }

      .kyc-section-heading h2 {
        font-size: 21px;
      }
    }

    @media (max-width: 480px) {
      .kyc-page {
        padding: 8px;
      }

      .kyc-header {
        padding: 21px 16px;
      }

      .kyc-header h1 {
        font-size: 27px;
      }

      .kyc-header p {
        font-size: 14px;
      }

      .kyc-all-fields {
        padding: 13px;
      }

      .kyc-card {
        padding: 17px;
      }

      .kyc-card h3 {
        font-size: 18px;
      }

      .kyc-completion {
        width: 100%;
        justify-content: center;
      }

      .kyc-footer-note {
        padding: 14px;
      }
    }
  `}</style>
);

export default BasicDatatable;
